<script>
  import { onMount } from "svelte";
  import api from "../../services/api.js";
  import { user } from "../../stores/auth.js";

  let bankAccounts = [];
  let transfers = [];
  let loading = true;
  let error = null;

  // Account balance
  let availableCash = 0;
  let loadingBalance = false;

  // Modal states
  let showAddBankModal = false;
  let showDepositModal = false;
  let showWithdrawModal = false;
  let selectedAccount = null;

  // Form states
  let newBankForm = {
    account_owner_name: "",
    bank_account_type: "CHECKING",
    bank_account_number: "",
    bank_routing_number: "",
    nickname: "",
  };
  let transferAmount = "";
  let formLoading = false;
  let formError = null;
  let formSuccess = null;

  // Bank account validation states
  let routingNumberTouched = false;
  let accountNumberTouched = false;

  // Validate ABA routing number using checksum algorithm
  // The checksum is: 3*(d1 + d4 + d7) + 7*(d2 + d5 + d8) + (d3 + d6 + d9) mod 10 == 0
  function validateRoutingNumber(routingNumber) {
    // Remove any non-digit characters
    const cleaned = routingNumber.replace(/\D/g, '');

    if (cleaned.length !== 9) {
      return { valid: false, message: "Routing number must be exactly 9 digits" };
    }

    // ABA checksum validation
    const digits = cleaned.split('').map(Number);
    const checksum = (
      3 * (digits[0] + digits[3] + digits[6]) +
      7 * (digits[1] + digits[4] + digits[7]) +
      (digits[2] + digits[5] + digits[8])
    ) % 10;

    if (checksum !== 0) {
      return { valid: false, message: "Invalid routing number - please verify and try again" };
    }

    return { valid: true, message: "" };
  }

  // Validate bank account number
  function validateAccountNumber(accountNumber) {
    // Remove any non-digit characters
    const cleaned = accountNumber.replace(/\D/g, '');

    if (cleaned.length < 4) {
      return { valid: false, message: "Account number must be at least 4 digits" };
    }

    if (cleaned.length > 17) {
      return { valid: false, message: "Account number must not exceed 17 digits" };
    }

    return { valid: true, message: "" };
  }

  // Reactive validation for routing number
  $: routingNumberValidation = newBankForm.bank_routing_number
    ? validateRoutingNumber(newBankForm.bank_routing_number)
    : { valid: false, message: "" };

  // Reactive validation for account number
  $: accountNumberValidation = newBankForm.bank_account_number
    ? validateAccountNumber(newBankForm.bank_account_number)
    : { valid: false, message: "" };

  // Check if entire bank form is valid
  $: isBankFormValid =
    newBankForm.account_owner_name.trim() !== "" &&
    routingNumberValidation.valid &&
    accountNumberValidation.valid;

  // Handle routing number input - only allow digits
  function handleRoutingNumberInput(event) {
    const cleaned = event.target.value.replace(/\D/g, '').slice(0, 9);
    newBankForm.bank_routing_number = cleaned;
    routingNumberTouched = true;
  }

  // Handle account number input - only allow digits
  function handleAccountNumberInput(event) {
    const cleaned = event.target.value.replace(/\D/g, '').slice(0, 17);
    newBankForm.bank_account_number = cleaned;
    accountNumberTouched = true;
  }

  onMount(async () => {
    // Pre-fill account owner name from user data
    if ($user) {
      newBankForm.account_owner_name = $user.name || "";
    }
    await loadData();
  });

  async function loadData() {
    loading = true;
    error = null;
    try {
      const [achResponse, transfersResponse] = await Promise.all([
        api.getAchRelationships(),
        api.getTransfers(),
      ]);
      bankAccounts = achResponse.relationships || [];
      transfers = transfersResponse.transfers || [];
    } catch (err) {
      error = err.message;
    } finally {
      loading = false;
    }
  }

  async function handleAddBank() {
    // Mark fields as touched for validation display
    routingNumberTouched = true;
    accountNumberTouched = true;

    // Validate before submitting
    if (!isBankFormValid) {
      formError = "Please correct the errors in the form";
      return;
    }

    formLoading = true;
    formError = null;
    formSuccess = null;

    try {
      await api.createAchRelationship(newBankForm);
      formSuccess = "Bank account linked successfully!";
      showAddBankModal = false;

      // Reset form
      newBankForm = {
        account_owner_name: $user?.name || "",
        bank_account_type: "CHECKING",
        bank_account_number: "",
        bank_routing_number: "",
        nickname: "",
      };
      routingNumberTouched = false;
      accountNumberTouched = false;

      await loadData();
    } catch (err) {
      formError = err.message;
    } finally {
      formLoading = false;
    }
  }

  async function handleDeleteBank(accountId) {
    if (!confirm("Are you sure you want to unlink this bank account?")) {
      return;
    }

    try {
      await api.deleteAchRelationship(accountId);
      await loadData();
    } catch (err) {
      error = err.message;
    }
  }

  async function handleDeposit() {
    if (!selectedAccount || !transferAmount) return;

    formLoading = true;
    formError = null;
    formSuccess = null;

    try {
      const amount = parseFloat(transferAmount);
      if (isNaN(amount) || amount < 1) {
        throw new Error("Minimum deposit amount is $1");
      }

      await api.deposit(amount, selectedAccount.id);
      formSuccess = `Deposit of $${amount.toFixed(2)} initiated successfully!`;
      showDepositModal = false;
      transferAmount = "";
      selectedAccount = null;

      await loadData();
    } catch (err) {
      formError = err.message;
    } finally {
      formLoading = false;
    }
  }

  async function handleWithdraw() {
    if (!selectedAccount || !transferAmount) return;

    formLoading = true;
    formError = null;
    formSuccess = null;

    try {
      const amount = parseFloat(transferAmount);
      if (isNaN(amount) || amount < 1) {
        throw new Error("Minimum withdrawal amount is $1");
      }

      if (amount > availableCash) {
        throw new Error(`Insufficient funds. Available: $${availableCash.toFixed(2)}`);
      }

      await api.withdraw(amount, selectedAccount.id);
      formSuccess = `Withdrawal of $${amount.toFixed(2)} initiated successfully!`;
      showWithdrawModal = false;
      transferAmount = "";
      selectedAccount = null;

      await loadData();
    } catch (err) {
      formError = err.message;
    } finally {
      formLoading = false;
    }
  }

  function openDepositModal(account) {
    selectedAccount = account;
    transferAmount = "";
    formError = null;
    showDepositModal = true;
  }

  async function openWithdrawModal(account) {
    selectedAccount = account;
    transferAmount = "";
    formError = null;
    loadingBalance = true;
    showWithdrawModal = true;

    // Fetch available cash balance
    try {
      const portfolio = await api.getPortfolioSummary();
      availableCash = parseFloat(portfolio.cash || 0);
    } catch (err) {
      // If we can't get balance, set to 0 and show error
      availableCash = 0;
      formError = "Could not fetch available balance";
    } finally {
      loadingBalance = false;
    }
  }

  // Reactive validation for withdraw amount
  $: withdrawAmountNum = parseFloat(transferAmount) || 0;
  $: isWithdrawAmountValid = withdrawAmountNum >= 1 && withdrawAmountNum <= availableCash;
  $: withdrawValidationMessage =
    !transferAmount ? "" :
    withdrawAmountNum < 1 ? "Minimum withdrawal is $1.00" :
    withdrawAmountNum > availableCash ? `Exceeds available balance of $${availableCash.toFixed(2)}` :
    "";

  function formatDate(dateString) {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }

  function formatStatus(status) {
    const statusMap = {
      APPROVED: { text: "Active", class: "badge-success" },
      PENDING: { text: "Pending", class: "badge-warning" },
      REJECTED: { text: "Rejected", class: "badge-danger" },
      CANCELED: { text: "Canceled", class: "badge-neutral" },
    };
    return statusMap[status] || { text: status, class: "badge-neutral" };
  }

  function formatTransferStatus(status) {
    const statusMap = {
      QUEUED: { text: "Queued", class: "badge-neutral" },
      PENDING: { text: "Pending", class: "badge-warning" },
      SENT_TO_CLEARING: { text: "Processing", class: "badge-info" },
      APPROVED: { text: "Approved", class: "badge-success" },
      COMPLETE: { text: "Complete", class: "badge-success" },
      REJECTED: { text: "Rejected", class: "badge-danger" },
      CANCELED: { text: "Canceled", class: "badge-neutral" },
      RETURNED: { text: "Returned", class: "badge-danger" },
    };
    return statusMap[status] || { text: status, class: "badge-neutral" };
  }

  function formatAccountType(type) {
    return type === "CHECKING" ? "Checking" : type === "SAVINGS" ? "Savings" : type;
  }

  function maskAccountNumber(number) {
    if (!number) return "****";
    // Already masked from backend, just format
    return `****${number.slice(-4)}`;
  }
</script>

<header class="mb-6 flex flex-wrap items-start justify-between gap-4">
  <div>
    <h1 class="text-2xl font-bold text-ink">Banking</h1>
    <p class="text-sm text-ink-secondary mt-1">Link bank accounts and move money in and out of your brokerage account.</p>
  </div>
  <button
    on:click={() => {
      formError = null;
      showAddBankModal = true;
    }}
    class="btn btn-primary btn-sm"
  >
    <i class="fas fa-plus"></i>
    Link bank account
  </button>
</header>

<!-- Success message -->
{#if formSuccess}
  <div class="banner banner-success mb-6 flex items-center gap-2">
    <i class="fas fa-check-circle"></i>
    <span>{formSuccess}</span>
    <button on:click={() => formSuccess = null} class="ml-auto hover:opacity-70" aria-label="Dismiss">
      <i class="fas fa-times"></i>
    </button>
  </div>
{/if}

<!-- Error message -->
{#if error}
  <div class="banner banner-danger mb-6 flex items-center gap-2">
    <i class="fas fa-exclamation-circle"></i>
    <span>{error}</span>
  </div>
{/if}

<!-- Loading -->
{#if loading}
  <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
    <div class="skeleton h-52"></div>
    <div class="skeleton h-52"></div>
    <div class="skeleton h-52"></div>
  </div>
  <div class="skeleton h-64"></div>
{:else}
  <!-- Bank Accounts Grid -->
  {#if bankAccounts.length === 0}
    <div class="card card-pad mb-8 text-center py-12">
      <i class="fas fa-university text-4xl text-ink-tertiary mb-4"></i>
      <h3 class="text-lg font-semibold text-ink mb-1">No bank accounts linked</h3>
      <p class="text-sm text-ink-secondary mb-5">
        Link a bank account to deposit and withdraw funds from your brokerage account.
      </p>
      <button
        on:click={() => showAddBankModal = true}
        class="btn btn-outline mx-auto"
      >
        <i class="fas fa-plus"></i>
        Link your first bank account
      </button>
    </div>
  {:else}
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
      {#each bankAccounts as account}
        <div class="card card-pad">
          <div class="flex items-start justify-between gap-3 mb-4">
            <div class="flex items-center gap-3 min-w-0">
              <div class="shrink-0 w-10 h-10 rounded-xl bg-surface-variant flex items-center justify-center">
                <i class="fas fa-university text-ink-secondary"></i>
              </div>
              <div class="min-w-0">
                <h5 class="text-base font-medium text-ink truncate">
                  {account.nickname || "Bank Account"}
                </h5>
                <p class="text-sm text-ink-secondary">
                  {formatAccountType(account.bank_account_type)}
                </p>
              </div>
            </div>
            <span class="badge {formatStatus(account.status).class}">
              {formatStatus(account.status).text}
            </span>
          </div>

          <div class="border-t border-line pt-4 space-y-2">
            <div class="flex justify-between text-sm">
              <span class="text-ink-secondary">Account number</span>
              <span class="text-ink font-medium tnum">{maskAccountNumber(account.bank_account_number)}</span>
            </div>
            <div class="flex justify-between text-sm">
              <span class="text-ink-secondary">Routing number</span>
              <span class="text-ink font-medium tnum">{account.bank_routing_number}</span>
            </div>
            <div class="flex justify-between text-sm">
              <span class="text-ink-secondary">Account holder</span>
              <span class="text-ink font-medium">{account.account_owner_name}</span>
            </div>
          </div>

          <div class="flex gap-2 mt-4 pt-4 border-t border-line">
            <button
              on:click={() => openDepositModal(account)}
              disabled={account.status !== "APPROVED"}
              class="btn btn-outline btn-sm flex-1"
            >
              <i class="fas fa-arrow-down text-ink-tertiary"></i> Deposit
            </button>
            <button
              on:click={() => openWithdrawModal(account)}
              disabled={account.status !== "APPROVED"}
              class="btn btn-outline btn-sm flex-1"
            >
              <i class="fas fa-arrow-up text-ink-tertiary"></i> Withdraw
            </button>
            <button
              on:click={() => handleDeleteBank(account.id)}
              class="btn btn-ghost btn-sm text-red-600 hover:text-red-700"
              aria-label="Unlink bank account"
            >
              <i class="fas fa-trash"></i>
            </button>
          </div>
        </div>
      {/each}
    </div>
  {/if}

  <!-- Transfer History -->
  <div class="card overflow-hidden">
    <div class="px-6 py-4 border-b border-line">
      <h3 class="text-lg font-semibold text-ink">Transfer history</h3>
    </div>
    <div class="block w-full overflow-x-auto">
      {#if transfers.length === 0}
        <div class="text-center py-12">
          <i class="fas fa-exchange-alt text-4xl text-ink-tertiary mb-3"></i>
          <p class="text-sm text-ink-secondary">No transfers yet</p>
        </div>
      {:else}
        <table class="w-full border-collapse">
          <thead>
            <tr>
              <th class="th">Date</th>
              <th class="th">Type</th>
              <th class="th text-right">Amount</th>
              <th class="th">Status</th>
            </tr>
          </thead>
          <tbody>
            {#each transfers as transfer}
              <tr class="hover:bg-canvas">
                <td class="td text-ink-secondary">
                  {formatDate(transfer.created_at)}
                </td>
                <td class="td">
                  <span class="flex items-center gap-2">
                    {#if transfer.direction === "INCOMING"}
                      <i class="fas fa-arrow-down text-ink-tertiary"></i>
                      <span class="text-ink">Deposit</span>
                    {:else}
                      <i class="fas fa-arrow-up text-ink-tertiary"></i>
                      <span class="text-ink">Withdrawal</span>
                    {/if}
                  </span>
                </td>
                <td class="td text-right font-medium tnum {transfer.direction === 'INCOMING' ? 'text-emerald-600' : 'text-red-600'}">
                  ${parseFloat(transfer.amount || 0).toFixed(2)}
                </td>
                <td class="td">
                  <span class="badge {formatTransferStatus(transfer.status).class}">
                    {formatTransferStatus(transfer.status).text}
                  </span>
                </td>
              </tr>
            {/each}
          </tbody>
        </table>
      {/if}
    </div>
  </div>
{/if}

<!-- Add Bank Account Modal -->
{#if showAddBankModal}
  <div class="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
    <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
      <div class="fixed inset-0 bg-slate-900/40 transition-opacity" on:click={() => showAddBankModal = false}></div>

      <div class="relative inline-block align-bottom bg-surface border border-line rounded-2xl text-left overflow-hidden shadow-sm transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
        <div class="p-6">
          <div class="flex items-center justify-between mb-4">
            <h3 class="text-lg font-semibold text-ink" id="modal-title">
              Link bank account
            </h3>
            <button on:click={() => showAddBankModal = false} class="text-ink-tertiary hover:text-ink" aria-label="Close">
              <i class="fas fa-times"></i>
            </button>
          </div>

          {#if formError}
            <div class="banner banner-danger mb-4">
              <i class="fas fa-exclamation-circle mr-2"></i>
              {formError}
            </div>
          {/if}

          <form on:submit|preventDefault={handleAddBank}>
            <div class="mb-4">
              <label class="label" for="account_owner_name">
                Account holder name
              </label>
              <input
                type="text"
                id="account_owner_name"
                bind:value={newBankForm.account_owner_name}
                required
                class="input"
                placeholder="John Doe"
              />
            </div>

            <div class="mb-4">
              <label class="label" for="bank_account_type">
                Account type
              </label>
              <select
                id="bank_account_type"
                bind:value={newBankForm.bank_account_type}
                required
                class="select"
              >
                <option value="CHECKING">Checking</option>
                <option value="SAVINGS">Savings</option>
              </select>
            </div>

            <div class="mb-4">
              <label class="label" for="bank_routing_number">
                Routing number
              </label>
              <input
                type="text"
                id="bank_routing_number"
                value={newBankForm.bank_routing_number}
                on:input={handleRoutingNumberInput}
                on:blur={() => routingNumberTouched = true}
                required
                maxlength="9"
                inputmode="numeric"
                pattern="[0-9]*"
                class="input tnum {routingNumberTouched && !routingNumberValidation.valid && newBankForm.bank_routing_number ? 'input-error' : ''} {routingNumberTouched && routingNumberValidation.valid ? 'ring-2 ring-moira ring-inset' : ''}"
                placeholder="123456789"
              />
              {#if routingNumberTouched && routingNumberValidation.message}
                <p class="help-error flex items-center gap-1">
                  <i class="fas fa-exclamation-circle"></i>
                  {routingNumberValidation.message}
                </p>
              {:else if routingNumberTouched && routingNumberValidation.valid}
                <p class="text-sm text-emerald-600 mt-1.5 flex items-center gap-1">
                  <i class="fas fa-check-circle"></i>
                  Valid routing number
                </p>
              {:else}
                <p class="text-xs text-ink-tertiary mt-1.5">9-digit ABA routing number from your bank</p>
              {/if}
            </div>

            <div class="mb-4">
              <label class="label" for="bank_account_number">
                Account number
              </label>
              <input
                type="text"
                id="bank_account_number"
                value={newBankForm.bank_account_number}
                on:input={handleAccountNumberInput}
                on:blur={() => accountNumberTouched = true}
                required
                maxlength="17"
                inputmode="numeric"
                pattern="[0-9]*"
                class="input tnum {accountNumberTouched && !accountNumberValidation.valid && newBankForm.bank_account_number ? 'input-error' : ''} {accountNumberTouched && accountNumberValidation.valid ? 'ring-2 ring-moira ring-inset' : ''}"
                placeholder="Enter your account number"
              />
              {#if accountNumberTouched && accountNumberValidation.message}
                <p class="help-error flex items-center gap-1">
                  <i class="fas fa-exclamation-circle"></i>
                  {accountNumberValidation.message}
                </p>
              {:else if accountNumberTouched && accountNumberValidation.valid}
                <p class="text-sm text-emerald-600 mt-1.5 flex items-center gap-1">
                  <i class="fas fa-check-circle"></i>
                  Valid account number format
                </p>
              {:else}
                <p class="text-xs text-ink-tertiary mt-1.5">4-17 digit account number</p>
              {/if}
            </div>

            <div class="mb-4">
              <label class="label" for="nickname">
                Nickname (optional)
              </label>
              <input
                type="text"
                id="nickname"
                bind:value={newBankForm.nickname}
                class="input"
                placeholder="e.g., Personal Checking"
              />
            </div>

            <div class="flex justify-end gap-2 mt-6">
              <button
                type="button"
                on:click={() => showAddBankModal = false}
                class="btn btn-outline btn-sm"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={formLoading || !isBankFormValid}
                class="btn btn-primary btn-sm"
              >
                {#if formLoading}
                  <i class="fas fa-spinner fa-spin"></i>
                {/if}
                Link account
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
{/if}

<!-- Deposit Modal -->
{#if showDepositModal && selectedAccount}
  <div class="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
    <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
      <div class="fixed inset-0 bg-slate-900/40 transition-opacity" on:click={() => showDepositModal = false}></div>

      <div class="relative inline-block align-bottom bg-surface border border-line rounded-2xl text-left overflow-hidden shadow-sm transform transition-all sm:my-8 sm:align-middle sm:max-w-md sm:w-full">
        <div class="p-6">
          <div class="flex items-center justify-between mb-4">
            <h3 class="text-lg font-semibold text-ink" id="modal-title">
              Deposit funds
            </h3>
            <button on:click={() => showDepositModal = false} class="text-ink-tertiary hover:text-ink" aria-label="Close">
              <i class="fas fa-times"></i>
            </button>
          </div>

          {#if formError}
            <div class="banner banner-danger mb-4">
              <i class="fas fa-exclamation-circle mr-2"></i>
              {formError}
            </div>
          {/if}

          <div class="rounded-xl bg-surface-variant p-4 mb-4">
            <p class="text-sm text-ink">
              <span class="font-semibold">From:</span> {selectedAccount.nickname || "Bank Account"}
            </p>
            <p class="text-xs text-ink-tertiary mt-0.5">
              {formatAccountType(selectedAccount.bank_account_type)} &middot; {maskAccountNumber(selectedAccount.bank_account_number)}
            </p>
          </div>

          <form on:submit|preventDefault={handleDeposit}>
            <div class="mb-4">
              <label class="label" for="deposit_amount">
                Amount to deposit
              </label>
              <div class="relative">
                <span class="absolute left-4 top-1/2 -translate-y-1/2 text-ink-tertiary text-sm">$</span>
                <input
                  type="number"
                  id="deposit_amount"
                  bind:value={transferAmount}
                  required
                  min="1"
                  step="0.01"
                  class="input tnum pl-8"
                  placeholder="0.00"
                />
              </div>
              <p class="text-xs text-ink-tertiary mt-1.5">Minimum deposit: $1.00</p>
            </div>

            <div class="flex justify-end gap-2 mt-6">
              <button
                type="button"
                on:click={() => showDepositModal = false}
                class="btn btn-outline btn-sm"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={formLoading}
                class="btn btn-primary btn-sm"
              >
                {#if formLoading}
                  <i class="fas fa-spinner fa-spin"></i>
                {/if}
                Deposit
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
{/if}

<!-- Withdraw Modal -->
{#if showWithdrawModal && selectedAccount}
  <div class="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
    <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
      <div class="fixed inset-0 bg-slate-900/40 transition-opacity" on:click={() => showWithdrawModal = false}></div>

      <div class="relative inline-block align-bottom bg-surface border border-line rounded-2xl text-left overflow-hidden shadow-sm transform transition-all sm:my-8 sm:align-middle sm:max-w-md sm:w-full">
        <div class="p-6">
          <div class="flex items-center justify-between mb-4">
            <h3 class="text-lg font-semibold text-ink" id="modal-title">
              Withdraw funds
            </h3>
            <button on:click={() => showWithdrawModal = false} class="text-ink-tertiary hover:text-ink" aria-label="Close">
              <i class="fas fa-times"></i>
            </button>
          </div>

          {#if formError}
            <div class="banner banner-danger mb-4">
              <i class="fas fa-exclamation-circle mr-2"></i>
              {formError}
            </div>
          {/if}

          <div class="rounded-xl bg-surface-variant p-4 mb-4">
            <p class="text-sm text-ink">
              <span class="font-semibold">To:</span> {selectedAccount.nickname || "Bank Account"}
            </p>
            <p class="text-xs text-ink-tertiary mt-0.5">
              {formatAccountType(selectedAccount.bank_account_type)} &middot; {maskAccountNumber(selectedAccount.bank_account_number)}
            </p>
          </div>

          <!-- Available Balance Display -->
          <div class="rounded-xl border border-line p-4 mb-4">
            <div class="flex items-center justify-between gap-3">
              <span class="text-sm text-ink-secondary">Available to withdraw</span>
              {#if loadingBalance}
                <span class="text-sm text-ink-tertiary">
                  <i class="fas fa-spinner fa-spin mr-1"></i>
                  Loading...
                </span>
              {:else}
                <span class="text-xl font-bold text-ink tnum">${availableCash.toFixed(2)}</span>
              {/if}
            </div>
            <p class="text-xs text-ink-tertiary mt-1">Cash balance in your brokerage account</p>
          </div>

          <form on:submit|preventDefault={handleWithdraw}>
            <div class="mb-4">
              <label class="label" for="withdraw_amount">
                Amount to withdraw
              </label>
              <div class="relative">
                <span class="absolute left-4 top-1/2 -translate-y-1/2 text-ink-tertiary text-sm">$</span>
                <input
                  type="number"
                  id="withdraw_amount"
                  bind:value={transferAmount}
                  required
                  min="1"
                  max={availableCash}
                  step="0.01"
                  class="input tnum pl-8 {withdrawValidationMessage ? 'input-error' : ''}"
                  placeholder="0.00"
                />
              </div>
              {#if withdrawValidationMessage}
                <p class="help-error">{withdrawValidationMessage}</p>
              {:else}
                <p class="text-xs text-ink-tertiary mt-1.5">Minimum withdrawal: $1.00</p>
              {/if}

              <!-- Quick amount buttons -->
              {#if availableCash >= 1}
                <div class="flex flex-wrap gap-2 mt-3">
                  {#if availableCash >= 25}
                    <button
                      type="button"
                      on:click={() => transferAmount = "25"}
                      class="rounded-lg px-3 py-1.5 text-xs font-medium tnum bg-surface-variant text-ink-secondary hover:bg-line hover:text-ink transition-colors"
                    >
                      $25
                    </button>
                  {/if}
                  {#if availableCash >= 50}
                    <button
                      type="button"
                      on:click={() => transferAmount = "50"}
                      class="rounded-lg px-3 py-1.5 text-xs font-medium tnum bg-surface-variant text-ink-secondary hover:bg-line hover:text-ink transition-colors"
                    >
                      $50
                    </button>
                  {/if}
                  {#if availableCash >= 100}
                    <button
                      type="button"
                      on:click={() => transferAmount = "100"}
                      class="rounded-lg px-3 py-1.5 text-xs font-medium tnum bg-surface-variant text-ink-secondary hover:bg-line hover:text-ink transition-colors"
                    >
                      $100
                    </button>
                  {/if}
                  <button
                    type="button"
                    on:click={() => transferAmount = availableCash.toFixed(2)}
                    class="rounded-lg px-3 py-1.5 text-xs font-semibold tnum bg-surface-variant text-ink hover:bg-line transition-colors"
                  >
                    Max (${availableCash.toFixed(2)})
                  </button>
                </div>
              {/if}
            </div>

            <div class="flex justify-end gap-2 mt-6">
              <button
                type="button"
                on:click={() => showWithdrawModal = false}
                class="btn btn-outline btn-sm"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={formLoading || !isWithdrawAmountValid || loadingBalance}
                class="btn btn-primary btn-sm"
              >
                {#if formLoading}
                  <i class="fas fa-spinner fa-spin"></i>
                {/if}
                Withdraw
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
{/if}
